/**
 * ForgotPasswordPage — citizen password recovery, two-step flow against
 * `service-identity`.
 *
 * Step 1 — `POST ${VITE_IDENTITY_BASE_URL}/v1/auth/password-reset/request`
 *          with `{identifier: <email-or-phone>}`. Always 202 (we never reveal
 *          whether the identifier matched). In dev mode (IDENTITY_AUTOVERIFY_
 *          REGISTRATION=true) the response carries the reset token in
 *          `devToken`; we stash it in component state and skip straight to
 *          the new-password step. In prod the user receives the token via the
 *          email/SMS link they click — that link lands them on the same page
 *          with `?token=...` in the URL, which we read on mount.
 *
 * Step 2 — `POST ${VITE_IDENTITY_BASE_URL}/v1/auth/password-reset/confirm`
 *          with `{token, newPassword}`. The token comes from either the dev
 *          response or the URL query string; the new password follows the
 *          same policy mirrored on the registration page (12+ chars, upper,
 *          digit, symbol).
 *
 * No fake OTP step: SMS/email delivery is a deferred follow-up, and the
 * 6-digit OTP placeholder in the previous design served no real purpose.
 */
import { type FormEvent, type JSX, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Mail,
  Lock,
  KeyRound,
  CheckCircle2,
  Circle,
  XCircle,
  Loader2,
  ArrowLeft,
  Eye,
  EyeOff,
} from 'lucide-react';

import { env } from '../../lib/env';

const PASSWORD_RULES: readonly { label: string; test: (pw: string) => boolean }[] = [
  { label: 'At least 12 characters', test: (pw) => pw.length >= 12 },
  { label: 'One uppercase letter (A–Z)', test: (pw) => /\p{Lu}/u.test(pw) },
  { label: 'One number (0–9)', test: (pw) => /\p{Nd}/u.test(pw) },
  { label: 'One symbol (!@#$ etc.)', test: (pw) => /[^A-Za-z0-9]/.test(pw) },
];

interface PasswordPolicyChecklistProps {
  readonly password: string;
}

function PasswordPolicyChecklist({ password }: PasswordPolicyChecklistProps): JSX.Element {
  return (
    <ul className="mt-2 space-y-1" aria-label="Password requirements">
      {PASSWORD_RULES.map((rule) => {
        const met = rule.test(password);
        const tone = password.length === 0
          ? 'text-gray-400'
          : met
          ? 'text-green-600'
          : 'text-red-500';
        const Icon = met ? CheckCircle2 : password.length === 0 ? Circle : XCircle;
        return (
          <li key={rule.label} className={`flex items-center gap-2 text-xs ${tone}`}>
            <Icon className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
            <span>{rule.label}</span>
          </li>
        );
      })}
    </ul>
  );
}

export function ForgotPasswordPage(): JSX.Element {
  const [searchParams] = useSearchParams();
  // If we landed via the email/SMS link, the token is in `?token=...` and we
  // jump straight to step 2.
  const tokenFromLink = searchParams.get('token') ?? '';
  const [step, setStep] = useState<number>(tokenFromLink ? 2 : 1);
  const [contact, setContact] = useState<string>('');
  const [resetToken, setResetToken] = useState<string>(tokenFromLink);
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirm, setShowConfirm] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [touched, setTouched] = useState<{ contact?: boolean; password?: boolean; confirm?: boolean }>({});
  // True when we got back a `devToken` from step 1 — the user can reset
  // immediately. In prod they need to wait for the email/SMS link.
  const [devTokenIssued, setDevTokenIssued] = useState<boolean>(false);

  const contactValid =
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact) || /^\+?\d{7,20}$/.test(contact);
  const passwordPolicyOk =
    newPassword.length >= 12 &&
    /\p{Lu}/u.test(newPassword) &&
    /\p{Nd}/u.test(newPassword) &&
    /[^A-Za-z0-9]/.test(newPassword);
  const passwordsMatch = newPassword === confirmPassword && newPassword.length > 0;

  async function handleSendCode(e: FormEvent): Promise<void> {
    e.preventDefault();
    setTouched((t) => ({ ...t, contact: true }));
    setError('');
    if (!contactValid) return;
    setLoading(true);
    try {
      const res = await fetch(`${env.identityBaseUrl}/v1/auth/password-reset/request`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', accept: 'application/json' },
        body: JSON.stringify({ identifier: contact }),
      });
      if (res.status >= 500) {
        setError('We\'re having trouble starting your reset. Please try again in a moment.');
        return;
      }
      // Either 202 (acknowledged, possibly with devToken) or anything else we
      // treat as a soft success to avoid leaking whether the account exists.
      let devToken: string | undefined;
      try {
        const body = (await res.json()) as { devToken?: unknown };
        if (typeof body.devToken === 'string' && body.devToken.length > 0) {
          devToken = body.devToken;
        }
      } catch {
        // 204/empty body — no devToken. Fine, prod path.
      }
      if (devToken) {
        setResetToken(devToken);
        setDevTokenIssued(true);
        setStep(2);
      } else {
        // Prod / no-match — show the "check your email/SMS" success screen
        // (step 4) without revealing whether the account exists.
        setStep(3);
      }
    } catch {
      setError('Could not reach the reset service. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }

  async function handleResetPassword(e: FormEvent): Promise<void> {
    e.preventDefault();
    setTouched((t) => ({ ...t, password: true, confirm: true }));
    setError('');
    if (!passwordPolicyOk || !passwordsMatch) return;
    if (!resetToken) {
      setError('Reset token missing. Restart from step 1.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${env.identityBaseUrl}/v1/auth/password-reset/confirm`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', accept: 'application/json' },
        body: JSON.stringify({ token: resetToken, newPassword }),
      });
      if (res.status === 204) {
        setStep(4);
        return;
      }
      if (res.status === 401) {
        setError('This reset link is invalid or has already been used. Request a new one.');
        return;
      }
      if (res.status === 400) {
        setError('That password isn\'t strong enough — try at least 12 characters with a mix of upper/lowercase, a number, and a symbol.');
        return;
      }
      if (res.status >= 500) {
        setError('We\'re having trouble resetting your password. Please try again in a moment.');
        return;
      }
      setError('Password reset failed. Please try again.');
    } catch {
      setError('Could not reach the reset service. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-xl bg-nipost-green flex items-center justify-center mx-auto mb-4 shadow-lg">
            <span className="text-white font-bold text-2xl">N</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Reset Password</h1>
          <p className="text-muted text-sm mt-1">UPMP Account Recovery</p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-border p-8">
          {step === 1 && (
            <>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-nipost-green-light flex items-center justify-center">
                  <KeyRound className="w-5 h-5 text-nipost-green" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-gray-900">Forgot your password?</h2>
                  <p className="text-muted text-sm">Enter the email or phone on your account</p>
                </div>
              </div>

              {error && (
                <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm" role="alert">
                  {error}
                </div>
              )}

              <form onSubmit={handleSendCode} className="space-y-4" noValidate>
                <div>
                  <label htmlFor="fp-contact" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Email or Phone Number
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      id="fp-contact"
                      type="text"
                      value={contact}
                      onChange={(e) => setContact(e.target.value)}
                      onBlur={() => setTouched((t) => ({ ...t, contact: true }))}
                      placeholder="adamu@example.com or 08012345678"
                      className={`w-full pl-10 pr-4 py-2.5 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-nipost-green/20 focus:border-nipost-green transition-colors ${
                        touched.contact && !contactValid
                          ? 'border-red-400 bg-red-50/50'
                          : touched.contact && contactValid
                          ? 'border-green-400'
                          : 'border-border'
                      }`}
                    />
                  </div>
                  {touched.contact && !contactValid && contact.length > 0 && (
                    <p className="text-red-500 text-xs mt-1">Enter a valid email or phone number</p>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={loading || !contactValid}
                  className="w-full py-2.5 px-4 rounded-lg bg-nipost-green text-white font-medium text-sm hover:bg-nipost-green-dark transition-colors disabled:opacity-70 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Reset Link'
                  )}
                </button>
              </form>
            </>
          )}

          {step === 2 && (
            <>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-nipost-green-light flex items-center justify-center">
                  <Lock className="w-5 h-5 text-nipost-green" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-gray-900">Set new password</h2>
                  <p className="text-muted text-sm">Choose a strong password for your account</p>
                </div>
              </div>

              {devTokenIssued && (
                <div className="mb-4 px-4 py-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs" role="status">
                  <span className="font-semibold">Dev mode:</span> reset token was returned directly so you can complete the reset without an email/SMS link.
                </div>
              )}

              {error && (
                <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm" role="alert">
                  {error}
                </div>
              )}

              <form onSubmit={handleResetPassword} className="space-y-4" noValidate>
                <div>
                  <label htmlFor="fp-new-password" className="block text-sm font-medium text-gray-700 mb-1.5">New Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      id="fp-new-password"
                      type={showPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      onBlur={() => setTouched((t) => ({ ...t, password: true }))}
                      placeholder="Min. 12 chars, with A–Z, 0–9, symbol"
                      className={`w-full pl-10 pr-11 py-2.5 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-nipost-green/20 focus:border-nipost-green transition-colors ${
                        touched.password && !passwordPolicyOk
                          ? 'border-red-400 bg-red-50/50'
                          : touched.password && passwordPolicyOk
                          ? 'border-green-400'
                          : 'border-border'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <PasswordPolicyChecklist password={newPassword} />
                </div>

                <div>
                  <label htmlFor="fp-confirm-password" className="block text-sm font-medium text-gray-700 mb-1.5">Confirm Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      id="fp-confirm-password"
                      type={showConfirm ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      onBlur={() => setTouched((t) => ({ ...t, confirm: true }))}
                      placeholder="Re-enter your password"
                      className={`w-full pl-10 pr-11 py-2.5 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-nipost-green/20 focus:border-nipost-green transition-colors ${
                        touched.confirm && !passwordsMatch
                          ? 'border-red-400 bg-red-50/50'
                          : confirmPassword.length > 0 && passwordsMatch
                          ? 'border-green-400'
                          : 'border-border'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm(!showConfirm)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      aria-label={showConfirm ? 'Hide password' : 'Show password'}
                    >
                      {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {touched.confirm && !passwordsMatch && confirmPassword.length > 0 && (
                    <p className="text-red-500 text-xs mt-1">Passwords do not match</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading || !passwordPolicyOk || !passwordsMatch}
                  className="w-full py-2.5 px-4 rounded-lg bg-nipost-green text-white font-medium text-sm hover:bg-nipost-green-dark transition-colors disabled:opacity-70 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Resetting...
                    </>
                  ) : (
                    'Reset Password'
                  )}
                </button>
              </form>
            </>
          )}

          {step === 3 && (
            <div className="text-center py-6">
              <div className="w-16 h-16 rounded-full bg-nipost-green-light flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-nipost-green" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Check your inbox</h3>
              <p className="text-muted text-sm mb-6">
                If an account matches that email or phone, we\'ve sent a reset link. Click it to choose a new password.
              </p>
              <Link
                to="/auth/login"
                className="inline-flex items-center justify-center w-full py-2.5 px-4 rounded-lg bg-nipost-green text-white font-medium text-sm hover:bg-nipost-green-dark transition-colors"
              >
                Back to Sign In
              </Link>
            </div>
          )}

          {step === 4 && (
            <div className="text-center py-6">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Password Reset Successful</h3>
              <p className="text-muted text-sm mb-6">
                Your password has been updated. You can now sign in with your new password.
              </p>
              <Link
                to="/auth/login"
                className="inline-flex items-center justify-center w-full py-2.5 px-4 rounded-lg bg-nipost-green text-white font-medium text-sm hover:bg-nipost-green-dark transition-colors"
              >
                Back to Sign In
              </Link>
            </div>
          )}
        </div>

        {step !== 4 && (
          <p className="text-center text-sm text-muted mt-6">
            <Link
              to="/auth/login"
              className="text-nipost-green hover:text-nipost-green-dark font-medium inline-flex items-center gap-1"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back to Sign In
            </Link>
          </p>
        )}
      </div>
    </div>
  );
}
